import { PDFDocument } from 'pdf-lib';

/** Read the page count of a PDF file. */
export const getPdfPageCount = async (file: File): Promise<number> => {
  const bytes = await file.arrayBuffer();
  const doc = await PDFDocument.load(bytes);
  return doc.getPageCount();
};

/**
 * Parse a 1-based page-range string like "1-3, 5, 8-10" into a de-duplicated,
 * order-preserving list of 0-based page indices. Throws on invalid input or
 * pages outside [1, pageCount].
 */
export const parsePageRanges = (input: string, pageCount: number): number[] => {
  const indices: number[] = [];
  const seen = new Set<number>();

  const parts = input.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) {
    throw new Error('Enter at least one page or range (e.g. "1-3, 5").');
  }

  for (const part of parts) {
    const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
    const singleMatch = part.match(/^(\d+)$/);

    if (rangeMatch) {
      const start = parseInt(rangeMatch[1], 10);
      const end = parseInt(rangeMatch[2], 10);
      if (start < 1 || end < 1 || start > pageCount || end > pageCount) {
        throw new Error(`Pages must be between 1 and ${pageCount}.`);
      }
      const step = start <= end ? 1 : -1;
      for (let p = start; step > 0 ? p <= end : p >= end; p += step) {
        if (!seen.has(p)) { seen.add(p); indices.push(p - 1); }
      }
    } else if (singleMatch) {
      const p = parseInt(singleMatch[1], 10);
      if (p < 1 || p > pageCount) {
        throw new Error(`Pages must be between 1 and ${pageCount}.`);
      }
      if (!seen.has(p)) { seen.add(p); indices.push(p - 1); }
    } else {
      throw new Error(`"${part}" is not a valid page or range.`);
    }
  }

  return indices;
};

/** Merge multiple PDF files into one, in the order provided. */
export const mergePdfs = async (files: File[]): Promise<Blob> => {
  if (files.length < 2) {
    throw new Error('Select at least two PDF files to merge.');
  }

  const merged = await PDFDocument.create();
  for (const file of files) {
    const bytes = await file.arrayBuffer();
    const doc = await PDFDocument.load(bytes);
    const pages = await merged.copyPages(doc, doc.getPageIndices());
    pages.forEach((page) => merged.addPage(page));
  }

  const out = await merged.save();
  return new Blob([out], { type: 'application/pdf' });
};

/**
 * Build a new PDF from the given file using a list of 0-based page indices,
 * in the specified order. Powers both "extract/split" and "reorder".
 */
export const buildPdfFromPages = async (file: File, pageIndices: number[]): Promise<Blob> => {
  if (pageIndices.length === 0) {
    throw new Error('No pages selected.');
  }
  const bytes = await file.arrayBuffer();
  const source = await PDFDocument.load(bytes);
  const out = await PDFDocument.create();
  const copied = await out.copyPages(source, pageIndices);
  copied.forEach((page) => out.addPage(page));
  const saved = await out.save();
  return new Blob([saved], { type: 'application/pdf' });
};

/** Extract the pages described by a range string into a new PDF. */
export const extractPages = async (file: File, ranges: string): Promise<Blob> => {
  const pageCount = await getPdfPageCount(file);
  const indices = parsePageRanges(ranges, pageCount);
  return buildPdfFromPages(file, indices);
};

/**
 * Reorder a PDF using a full permutation string like "3,1,2". The order must
 * reference every page exactly once.
 */
export const reorderPages = async (file: File, order: string): Promise<Blob> => {
  const pageCount = await getPdfPageCount(file);
  const indices = parsePageRanges(order, pageCount);
  if (indices.length !== pageCount) {
    throw new Error(`Provide all ${pageCount} pages exactly once (e.g. "3, 1, 2").`);
  }
  return buildPdfFromPages(file, indices);
};
